import React, { useContext, useState, useMemo, useRef } from "react";
import {
  FaSearch,
  FaFilePdf,
  FaFileExcel,
  FaFileCsv,
  FaEye,
  FaTrash,
  FaArrowLeft,
  FaArrowRight,
  FaCheckCircle,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import ModalDetalhes from "./ModalDetalhes";
import { AlunoContext } from "../context/AlunoContext";
import activateSound from "../assets/sounds/activate.mp3";
import deleteSound from "../assets/sounds/delete.mp3";
import { exportarAlunos } from "../utils/exportarPreRegisto";
import ModalBoleto from './ModalBoleto';
import "./PreRegistoAluno.css";

const PreRegistoAluno = () => {
  const { inativos, ativarAluno, fetchAlunos } = useContext(AlunoContext);
  const [filtroNome, setFiltroNome] = useState("");
  const [filtroClasse, setFiltroClasse] = useState("");
  const [alunoSelecionado, setAlunoSelecionado] = useState(null);
  const [mostrarModalBoleto, setMostrarModalBoleto] = useState(false);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [exportando, setExportando] = useState(false);
  const [alunoModalBoleto, setAlunoModalBoleto] = useState(null);
  const navigate = useNavigate();
  const alunosPorPagina = 10;

  const audioRef = useRef(null);
  const playSound = (src) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    audioRef.current = new Audio(src);
    audioRef.current.play();
  };

  const alunosFiltrados = useMemo(() => {
    return inativos.filter(
      (aluno) =>
        (
          aluno.nome_completo.toLowerCase().includes(filtroNome.toLowerCase()) ||
          (aluno.codigo_aluno || "").toLowerCase().includes(filtroNome.toLowerCase())
        ) &&
        (filtroClasse === "" || aluno.classe === filtroClasse)
    );
  }, [inativos, filtroNome, filtroClasse]);

  const total = alunosFiltrados.length;
  const totalMasculinos = alunosFiltrados.filter(
    (a) => a.genero && a.genero.toLowerCase() === "masculino"
  ).length;
  const totalFemininos = alunosFiltrados.filter(
    (a) => a.genero && a.genero.toLowerCase() === "feminino"
  ).length;

  const totalPaginas = Math.ceil(alunosFiltrados.length / alunosPorPagina);

  const alunosPaginados = useMemo(() => {
    const start = (paginaAtual - 1) * alunosPorPagina;
    return alunosFiltrados.slice(start, start + alunosPorPagina);
  }, [paginaAtual, alunosFiltrados]);

  const formatarCodigoAluno = (codigo) => {
    if (!codigo) return "----";
    const valor = typeof codigo === "string" ? codigo : String(codigo);
    if (valor.length === 8 && valor.startsWith("202")) return valor;
    return valor.padStart(8, "0");
  };

  const dadosPreRegisto = useMemo(() =>
    alunosFiltrados.map((aluno) => {
      const codigo = formatarCodigoAluno(aluno.codigo_aluno);

      return {
        Classe: aluno.classe || "—",
        Nome: aluno.nome_completo || "—",
        "Código do Aluno": codigo,
        BI: aluno.numero_bi || "—",
        Telefone: aluno.telefone_responsavel || "—",
        Status: aluno.ativo ? "Ativo" : "Inativo",
      };
    }),
    [alunosFiltrados]
  );

  const onExportar = async (formato) => {
    if (dadosPreRegisto.length === 0) {
      alert("Nenhum aluno para exportar.");
      return;
    }

    try {
      setExportando(true);
      await exportarAlunos(dadosPreRegisto, formato, "Pré-Registo de Alunos", "ACADE_PreRegisto");
    } catch (error) {
      alert("Erro ao exportar dados: " + error.message);
      console.error(error);
    } finally {
      setExportando(false);
    }
  };

  const handleAtivar = async (id, nome) => {
    const confirmar = window.confirm(
      `⚠️ Atenção!\n\nDeseja realmente ATIVAR o aluno:\n\n🧑 ${nome}?`
    );
    if (confirmar) {
      await ativarAluno(id);
      playSound(activateSound);
      fetchAlunos();
    }
  };

  const handleEliminar = async (id, nome) => {
    const confirmar = window.confirm(
      `❌ Confirmação de Eliminação\n\nTem certeza que deseja ELIMINAR o aluno:\n\n🧑 ${nome}?\n\nEssa ação é irreversível.`
    );
    if (!confirmar) return;

    playSound(deleteSound);

    try {
      const res = await fetch(`/api/alunos/${id}`, { method: "DELETE" });

      if (res.ok) {
        alert(`✅ Aluno ${nome} eliminado com sucesso.`);
        fetchAlunos();
      } else {
        const contentType = res.headers.get("content-type");
        let mensagemErro = `Erro ${res.status}`;

        if (contentType?.includes("application/json")) {
          const erro = await res.json();
          mensagemErro = erro.message || mensagemErro;
        } else {
          mensagemErro = await res.text();
        }

        console.warn("Resposta inesperada ao eliminar:", mensagemErro);
        alert(`❌ Erro ao eliminar o aluno ${nome}: ${mensagemErro}`);
      }
    } catch (err) {
      console.error("Erro ao eliminar aluno:", err);
      alert(`❌ Erro na conexão com o servidor.`);
    }
  };

  function handleAbrirModalBoleto(aluno) {
    setAlunoSelecionado(null);
    setAlunoModalBoleto(aluno);
    setMostrarModalBoleto(true);
  }

  const handleEditarAluno = async (dadosAtualizados) => {
    try {
      const res = await fetch(`/api/alunos/${dadosAtualizados.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dadosAtualizados),
      });

      if (res.ok) {
        alert("✅ Aluno atualizado com sucesso.");
        fetchAlunos();
        setAlunoSelecionado(null);
      } else {
        alert("❌ Erro ao atualizar aluno.");
      }
    } catch (error) {
      console.error("Erro:", error);
      alert("❌ Erro de conexão ao editar.");
    }
  };

  return (
    <div className="lista-alunos">
      <h2>Alunos Pré-registados no sistema</h2>

      <div className="resumo-alunos">
        Total: <span className="total">{total}</span> Alunos — 
        Masculinos: <span className="masculino">{totalMasculinos}</span> — 
        Femininos: <span className="feminino">{totalFemininos}</span>
      </div>

      <div className="filtro-nome">
        <FaSearch className="icone-pesquisa" />
        <input
          type="text"
          placeholder="Buscar por nome ou código"
          value={filtroNome}
          onChange={(e) => setFiltroNome(e.target.value)}
          disabled={exportando}
        />
        <select
          value={filtroClasse}
          onChange={(e) => setFiltroClasse(e.target.value)}
          disabled={exportando}
        >
          <option value="">Todas as Classes</option>
          {[...new Set(inativos.map((a) => a.classe))]
            .sort()
            .map((classe) => (
              <option key={classe} value={classe}>
                {classe}
              </option>
            ))}
        </select>

        <button
          onClick={() => onExportar("pdf")}
          title="Exportar PDF"
          disabled={exportando || dadosPreRegisto.length === 0}
          className="btn-exportar btn-pdf"
        >
          <FaFilePdf /> PDF
        </button>
        <button
          onClick={() => onExportar("excel")}
          title="Exportar Excel"
          disabled={exportando || dadosPreRegisto.length === 0}
          className="btn-exportar btn-excel"
        >
          <FaFileExcel /> Excel
        </button>
        <button
          onClick={() => onExportar("csv")}
          title="Exportar CSV"
          disabled={exportando || dadosPreRegisto.length === 0}
          className="btn-exportar btn-csv"
        >
          <FaFileCsv /> CSV
        </button>
      </div>

      <div className="lista-container">
        {alunosPaginados.length > 0 ? (
          alunosPaginados.map((aluno) => (
            <div key={aluno.id} className="aluno-card">
              <span onClick={() => setAlunoSelecionado(aluno)} style={{ cursor: "pointer" }}>
                <FaEye /> {formatarCodigoAluno(aluno.codigo_aluno)} - {aluno.nome_completo}
              </span>

              <div className="acoes">
                <button
                  className="btn-ativar"
                  onClick={() => handleAtivar(aluno.id, aluno.nome_completo)}
                  title="Ativar Aluno"
                >
                  <FaCheckCircle />
                </button>
                <button
                  onClick={() => handleEliminar(aluno.id, aluno.nome_completo)}
                  title="Eliminar Aluno"
                >
                  <FaTrash color="red" />
                </button>

                <button
                  onClick={() => handleAbrirModalBoleto(aluno)}
                  title="Ver Boleto de Matrícula"
                >
                  📄
                </button>
              </div>
            </div>
          ))
        ) : (
          <p>Nenhum aluno pré-registado inativo.</p>
        )}
      </div>

      <div className="paginacao">
        {paginaAtual > 1 && (
          <FaArrowLeft
            onClick={() => setPaginaAtual(paginaAtual - 1)}
            style={{ cursor: "pointer" }}
          />
        )}
        <span>{paginaAtual}</span>
        {paginaAtual < totalPaginas && (
          <FaArrowRight
            onClick={() => setPaginaAtual(paginaAtual + 1)}
            style={{ cursor: "pointer" }}
          />
        )}
      </div>

      {alunoSelecionado && (
        <ModalDetalhes
          aluno={alunoSelecionado}
          onClose={() => setAlunoSelecionado(null)}
          onAtivar={handleAtivar}
          onEliminar={handleEliminar}
          onEditar={handleEditarAluno}
        />
      )}
      {mostrarModalBoleto && alunoModalBoleto && (
        <ModalBoleto
          aluno={alunoModalBoleto}
          onFechar={() => setMostrarModalBoleto(false)}
        />
      )}
    </div>
  );
};

export default PreRegistoAluno;
